import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from '../../img/logo.png';
import { Typography } from "@mui/material";
import { subdomain, getBackendUrl } from '../../auth/utilities';
import cookie from "react-cookies";
import "./VendorLogin.css";

export default function VendorLogin() {
    const [focusedInput, setFocusedInput] = useState(null);
    const [vendorEmail, setVendorEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [backendEmailError, setBackendEmailError] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [loadingVerify, setLoadingVerify] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [timeLeft, setTimeLeft] = useState(600);
    const totalTime = 600;
    const [otpValue, setOtpValue] = useState("");
    const [loading, setLoading] = useState(false);
    const csrfToken = cookie.load("csrftoken");
    const navigate = useNavigate();
    const location = useLocation();
    const backend = getBackendUrl();

    // Extract URL parameters dynamically when required later
    const queryParams = new URLSearchParams(location.search);

    const resetOtpProcess = useCallback(() => {
        setOtpSent(false);
        setOtpValue("");
        setVendorEmail("");
        setTimeLeft(600);
    }, []);

    useEffect(() => {
        if (otpSent && timeLeft === 0) {
            resetOtpProcess();
        }
    }, [timeLeft, otpSent, resetOtpProcess]);

    const progress = (timeLeft / totalTime) * 100;
    const formatTime = (seconds) => {
        const m = String(Math.floor(seconds / 60)).padStart(2, "0");
        const s = String(seconds % 60).padStart(2, "0");
        return `${m}:${s}`;
    };

    useEffect(() => {
        let timer;
        if (otpSent && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [otpSent, timeLeft]);

    const validateEmail = (value) => {
        if (!value.trim()) {
            setEmailError("Email is required");
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            setEmailError("Enter a valid email address");
            return false;
        }
        setEmailError("");
        return true;
    };

    useEffect(() => {
        const blockBack = () => {
            navigate("/vendor-assessment/", { replace: true });
        };
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", blockBack);
        return () => {
            window.removeEventListener("popstate", blockBack);
        };
    }, [navigate]);

    const handleGeneratingOtp = async () => {
        setBackendEmailError("");
        if (!validateEmail(vendorEmail)) return;

        const payload = { vendor_email: vendorEmail };
        try {
            setLoading(true);
            const response = await fetch(
                `${backend}${subdomain}/en/tprm/send-vendor-otp/`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const data = await response.json();
            if (!response.ok) {
                setBackendEmailError(data?.error || "Failed to send OTP");
                return;
            }
            if (data?.message) {
                setOtpSent(true);
                setTimeLeft(600);
            }
        } catch (error) {
            setBackendEmailError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        setLoadingVerify(true);
        setOtpError("");
        const payload = { vendor_email: vendorEmail, otp: otpValue };
        try {
            const response = await fetch(
                `${backend}${subdomain}/en/tprm/verify-vendor-otp/`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrfToken,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const data = await response.json();
            if (response.ok && data?.message) {
                // Navigate based on 'flow' parameter, passing ALL query params
                const flow = queryParams.get("flow") || "onboard";
                navigate(`/vendor-assessment/${flow}/${location.search}`, {
                    state: { vendorEmail },
                });
            } else {
                setOtpError(data?.error || "OTP verification failed");
            }
        } catch (error) {
            setOtpError("Something went wrong, please try again.");
        } finally {
            setLoadingVerify(false);
        }
    };

    return (
        <div
            className="vendor-login-container"
            onKeyDown={(e) => {
                if (e.key === "Enter" && !loading && !loadingVerify) {
                    if (otpSent) { handleVerifyOtp(); } else { handleGeneratingOtp(); }
                }
            }}>

            {/* LEFT — Login Form */}
            <div className="vendor-login-left-section">
                <div className="vendor-login-left-decor"></div>
                <div className="vendor-login-form-container">
                    <div className="vendor-login-form-box">
                        <h1 className="vendor-login-heading">Third-Party Vendor Verification</h1>
                        <p className="vendor-login-subheading">Verify your identity to access the assessment</p>

                        <div>
                            <div className="vendor-login-input-group">
                                <label className="vendor-login-label">Vendor Email</label>
                                <input
                                    type="text"
                                    placeholder="Enter your Email"
                                    value={vendorEmail}
                                    readOnly={otpSent}
                                    onChange={(e) => {
                                        setVendorEmail(e.target.value);
                                        if (emailError) validateEmail(e.target.value);
                                    }}
                                    onBlur={() => validateEmail(vendorEmail)}
                                    onFocus={() => setFocusedInput("vendorEmail")}
                                    className={`vendor-login-input ${focusedInput === "vendorEmail" ? "focused" : ""} ${emailError ? "error" : ""}`}
                                    style={{ borderColor: emailError || focusedInput === "vendorEmail" ? undefined : "#ccc" }}
                                />
                                {(emailError || backendEmailError) && (
                                    <div className="vendor-login-error-text">
                                        {emailError || backendEmailError}
                                    </div>
                                )}

                                {otpSent && (
                                    <div className="vendor-login-change-email-box">
                                        <span className="vendor-login-change-email-text">Entered wrong email? </span>
                                        <span
                                            role="button"
                                            className="vendor-login-change-email-link"
                                            onClick={resetOtpProcess}
                                        >
                                            Use a different email
                                        </span>
                                    </div>
                                )}

                                {otpSent && (
                                    <>
                                        <label className="vendor-login-label">Enter OTP</label>
                                        <input
                                            type="text"
                                            placeholder="Enter OTP"
                                            value={otpValue}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d{0,6}$/.test(value)) {
                                                    setOtpValue(value);
                                                    setOtpError("");
                                                }
                                            }}
                                            onFocus={() => setFocusedInput("otp")}
                                            onBlur={() => setFocusedInput(null)}
                                            className={`vendor-login-input ${focusedInput === "otp" ? "focused" : ""} ${otpError ? "error" : ""}`}
                                        />
                                        {otpError && (
                                            <p className="vendor-login-error-text" style={{ marginTop: "6px", fontSize: "13px" }}>
                                                {otpError}
                                            </p>
                                        )}
                                    </>
                                )}

                                {otpSent && (
                                    <>
                                        <div className="vendor-otp-timer-container">
                                            OTP valid for:
                                            <span className="vendor-otp-timer-value">
                                                {formatTime(timeLeft)}
                                            </span>
                                        </div>
                                        <div className="vendor-otp-progress-bar">
                                            <div className="vendor-otp-progress-fill" style={{ width: `${progress}%` }}></div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={otpSent ? handleVerifyOtp : handleGeneratingOtp}
                                disabled={loading || loadingVerify}
                                className="vendor-login-button"
                            >
                                {loading ? "Sending OTP..." : loadingVerify ? "Verifying..." : otpSent ? "Verify OTP" : "Generate OTP"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT — Welcome Panel */}
            <div className="vendor-login-right-section">
                <div className="vendor-star vendor-star-1"></div>
                <div className="vendor-star vendor-star-2"></div>
                <div className="vendor-star vendor-star-3"></div>
                <div className="vendor-star vendor-star-4"></div>
                <div className="vendor-star vendor-star-5"></div>
                <div className="vendor-star vendor-star-6"></div>
                <div className="vendor-login-content-box">
                    <div className="vendor-login-logo-container">
                        <img src={logo} alt="logo" className="vendor-login-logo" />
                    </div>
                    <Typography className="vendor-login-welcome-heading">Third-Party Risk Management (TPRM) Portal</Typography>
                    <p className="vendor-login-description">
                        Welcome to the WhizzC TPRM Portal. Complete your security questionnaire to help us evaluate compliance requirements. Your responses are confidential and play a crucial role in our third-party risk management process.
                    </p>
                </div>
            </div>
        </div>
    );
}
